// AppDownloadPage.jsx
// Route: /download?id=69eb4f934aca106a7c0c6b4c
// Public page — no auth required

import { useState, useEffect, useCallback, useRef } from 'react';

// ─── Config ───────────────────────────────────────────────
const API_BASE     = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL||'http://localhost:5000/v1';
const DEFAULT_ID   = '6a1d551de8a1f01af3351afc';
const FEATURE_ICONS = {
  'Push Notifications': '🔔',
  'Offline Mode':       '📶',
  'GPS':                '📍',
  'Camera':             '📷',
  'Biometric':          '🔐',
};
const TRACK_META = {
  production: { label: 'PRD', bg: 'rgba(34,197,94,0.15)',    color: '#86efac' },
  alpha:       { label: 'α',   bg: 'rgba(245,158,11,0.15)',   color: '#fcd34d' },
  beta:        { label: 'β',   bg: 'rgba(139,92,246,0.15)',   color: '#c4b5fd' },
};

// ─── Helpers ──────────────────────────────────────────────
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const getInstalledVer = (pkg) => {
  const sim = new URLSearchParams(window.location.search).get('installedVersion');
  if (sim) return parseInt(sim, 10);
  const v = localStorage.getItem('installed_' + pkg);
  return v ? parseInt(v, 10) : null;
};
const setInstalledVer = (pkg, code) =>
  localStorage.setItem('installed_' + pkg, String(code));

// ─── Sub-components ───────────────────────────────────────

const StatusDot = ({ color }) => (
  <span style={{ width:7, height:7, borderRadius:'50%', background:color, display:'inline-block', marginRight:5 }} />
);

const Tag = ({ label, variant = 'blue' }) => {
  const variants = {
    blue:   { bg:'rgba(25,118,210,0.18)',  color:'#90caf9', border:'rgba(25,118,210,0.3)'  },
    orange: { bg:'rgba(255,87,34,0.18)',   color:'#ff8a65', border:'rgba(255,87,34,0.3)'   },
    green:  { bg:'rgba(34,197,94,0.15)',   color:'#86efac', border:'rgba(34,197,94,0.25)'  },
    grey:   { bg:'rgba(255,255,255,0.06)', color:'#8892a4', border:'rgba(255,255,255,0.1)' },
  };
  const v = variants[variant] || variants.grey;
  return (
    <span style={{
      fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:12,
      letterSpacing:'0.05em', textTransform:'uppercase',
      background:v.bg, color:v.color, border:`1px solid ${v.border}`,
    }}>
      {label}
    </span>
  );
};

const UsageBar = ({ current, limit }) => {
  if (!limit) return (
    <span style={{ fontSize:12, color:'#8892a4', display:'flex', alignItems:'center', gap:5 }}>
      ∞ Unlimited
    </span>
  );
  const pct   = Math.min(100, Math.round((current / limit) * 100));
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:4 }}>
        <span style={{ color:'#8892a4' }}>{current} / {limit} devices</span>
        <span style={{ color, fontWeight:700 }}>{pct}%</span>
      </div>
      <div style={{ height:5, background:'rgba(255,255,255,0.08)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width .4s' }} />
      </div>
    </div>
  );
};

const Toast = ({ msg, type, visible }) => (
  <div style={{
    position:'fixed', bottom:24, left:'50%',
    transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(20px)',
    opacity: visible ? 1 : 0,
    transition: 'all .3s',
    padding:'12px 22px', borderRadius:12, fontWeight:600, fontSize:13,
    zIndex:9999, pointerEvents:'none', whiteSpace:'nowrap', maxWidth:'90vw',
    background: type==='success' ? '#14532d' : type==='error' ? '#450a0a' : '#1e3a5f',
    color:       type==='success' ? '#86efac' : type==='error' ? '#fca5a5' : '#90caf9',
    border:      type==='success' ? '1px solid #166534' : type==='error' ? '1px solid #7f1d1d' : '1px solid rgba(25,118,210,0.5)',
    boxShadow:'0 8px 32px rgba(0,0,0,0.4)',
  }}>
    {msg}
  </div>
);

// ─── Main Component ───────────────────────────────────────
export default function AppDownloadPage() {
  const searchParams = new URLSearchParams(window.location.search);
  const APP_ID       = searchParams.get('id') || DEFAULT_ID;
  const isAndroid    = /android/i.test(navigator.userAgent);

  const [app,          setApp]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [installedVer, setInstalledVerState] = useState(null);
  const [downloading,  setDownloading]  = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [progressMsg,  setProgressMsg]  = useState('');
  const [toast,        setToast]        = useState({ msg:'', type:'info', visible:false });
  const [apiBanner,    setApiBanner]    = useState(null);
  const progressRef = useRef(null);

  // ── Toast ────────────────────────────────────────────────
  const showToast = useCallback((msg, type='info', dur=3500) => {
    setToast({ msg, type, visible:true });
    setTimeout(() => setToast(t => ({ ...t, visible:false })), dur);
  }, []);

  // ── Fetch ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = searchParams.get('token') || localStorage.getItem('authToken') || localStorage.getItem('token') || null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      try {
        const res  = await fetch(`${API_BASE}/custom-apps/${APP_ID}`, { headers, mode:'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || 'API error');
        const data = json.data;
        setApp(data);
        const pkg = data.packageName || APP_ID;
        setInstalledVerState(getInstalledVer(pkg));
        document.title = `${data.appName} | Download`;
      } catch (err) {
        console.error('API error:', err.message);
        setApiBanner(`⚠️ ${err.message} — showing demo data`);
        // Demo fallback
        const demo = DEMO_DATA;
        setApp(demo);
        setInstalledVerState(getInstalledVer(demo.packageName));
        document.title = `${demo.appName} | Download`;
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line
  }, [APP_ID]);

  // ── Derived ──────────────────────────────────────────────
  const latestAPK = app
    ? (app.latestAPK || app.apkVersions?.find(v => v.isActive && v.track === 'production') || app.apkVersions?.[0] || null)
    : null;

  const installState = !latestAPK ? 'unavailable'
    : installedVer === null        ? 'not_installed'
    : installedVer < latestAPK.versionCode ? 'update'
    : 'installed';

  // ── Download ─────────────────────────────────────────────
  const triggerDownload = useCallback((dlUrl, filename, versionCode, isUpdate=false) => {
    if (downloading) return;
    setDownloading(true);
    setProgress(0);
    setProgressMsg(isUpdate ? 'Downloading update...' : 'Preparing download...');

    // Progress animation
    let pct = 0;
    progressRef.current = setInterval(() => {
      pct += Math.random() * 7;
      if (pct >= 95) { pct = 95; clearInterval(progressRef.current); }
      setProgress(Math.round(pct));
      setProgressMsg(filename);
    }, 200);

    // Trigger download
    const a = Object.assign(document.createElement('a'), {
      href: dlUrl, download: filename, target: '_blank',
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => {
      clearInterval(progressRef.current);
      setProgress(100);
      setProgressMsg(isUpdate ? 'Update downloaded! Tap APK to install.' : 'Downloaded! Tap APK to install.');

      const pkg = app?.packageName || APP_ID;
      setInstalledVer(pkg, versionCode);
      setInstalledVerState(versionCode);

      setTimeout(() => {
        setDownloading(false);
        setProgress(0);
        showToast(isUpdate ? '🔄 Update ready! Open file manager to install.' : '✅ APK downloaded! Tap the file to install.', 'success', 5000);
      }, 1200);
    }, 3000);
  }, [downloading, app, APP_ID, showToast]);

  const handleMainAction = () => {
    if (!latestAPK) return;
    const dlUrl = `${API_BASE}/custom-apps/${APP_ID}/apk/download`;
    if (installState === 'not_installed') {
      triggerDownload(dlUrl, latestAPK.fileName, latestAPK.versionCode);
    } else if (installState === 'update') {
      triggerDownload(dlUrl, latestAPK.fileName, latestAPK.versionCode, true);
    } else {
      openApp();
    }
  };

  const handleSpecificDownload = (apkId, filename, versionCode) => {
    const dlUrl = `${API_BASE}/custom-apps/${APP_ID}/apk/${apkId}/download`;
    triggerDownload(dlUrl, filename, versionCode);
  };

  const openApp = () => {
    if (!app?.packageName) return;
    if (isAndroid) {
      const iframe = Object.assign(document.createElement('iframe'), {
        style: 'display:none',
        src: `intent://${app.packageName}/#Intent;scheme=${app.packageName};package=${app.packageName};end`,
      });
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 500);
      showToast('Opening app...', 'info');
    } else {
      showToast('Open this page on your Android device to launch the app.', 'info');
    }
  };

  // ── CTA config ───────────────────────────────────────────
  const ctaConfig = {
    not_installed: {
      label: `Install  ${latestAPK?.fileSize ? `(${latestAPK.fileSize})` : ''}`,
      icon: '⬇️', style: { background:'linear-gradient(135deg,#1976D2,#0D47A1)', boxShadow:'0 8px 32px rgba(13,71,161,0.5)' },
    },
    update: {
      label: `Update to v${latestAPK?.versionName}  ${latestAPK?.fileSize ? `(${latestAPK.fileSize})` : ''}`,
      icon: '🔄', style: { background:'linear-gradient(135deg,#f59e0b,#d97706)', boxShadow:'0 8px 32px rgba(245,158,11,0.35)' },
    },
    installed: {
      label: 'Open App',
      icon: '🚀', style: { background:'linear-gradient(135deg,#22c55e,#16a34a)', boxShadow:'0 8px 32px rgba(34,197,94,0.35)' },
    },
    unavailable: {
      label: 'No APK Available', icon: '⚠️', style: { background:'rgba(255,255,255,0.06)', boxShadow:'none' },
    },
  };
  const cta = ctaConfig[installState] || ctaConfig.not_installed;

  const statusConfig = {
    not_installed: {
      icon:'📱', iconBg:'rgba(25,118,210,0.15)',
      title:'Not Installed', titleColor:'#90caf9',
      sub: latestAPK ? `v${latestAPK.versionName} · ${latestAPK.fileSize} available` : '',
      cardBorder:'rgba(25,118,210,0.25)', cardBg:'rgba(25,118,210,0.06)',
    },
    update: {
      icon:'🔄', iconBg:'rgba(245,158,11,0.15)',
      title:'Update Available', titleColor:'#fcd34d',
      sub: `Installed v${installedVer} → Latest v${latestAPK?.versionName} (${latestAPK?.versionCode})`,
      cardBorder:'rgba(245,158,11,0.25)', cardBg:'rgba(245,158,11,0.06)',
    },
    installed: {
      icon:'✅', iconBg:'rgba(34,197,94,0.15)',
      title:'Up to Date', titleColor:'#86efac',
      sub:`v${latestAPK?.versionName} installed — you have the latest version`,
      cardBorder:'rgba(34,197,94,0.2)', cardBg:'rgba(34,197,94,0.06)',
    },
    unavailable: {
      icon:'⚠️', iconBg:'rgba(255,255,255,0.06)',
      title:'No APK Available', titleColor:'#8892a4',
      sub:'No active APK found for this app.',
      cardBorder:'rgba(255,255,255,0.07)', cardBg:'transparent',
    },
  };
  const statusCfg = statusConfig[installState] || statusConfig.unavailable;

  // ── Styles ───────────────────────────────────────────────
  const S = {
    page: {
      minHeight:'100vh', background:'#07090f', color:'#f0f2f8',
      fontFamily:"'Epilogue', 'DM Sans', system-ui, sans-serif",
      position:'relative', overflowX:'hidden',
    },
    mesh: {
      position:'fixed', inset:0, zIndex:0, pointerEvents:'none',
      background:`
        radial-gradient(ellipse 60% 50% at 10% 20%, rgba(13,71,161,0.18) 0%, transparent 70%),
        radial-gradient(ellipse 50% 40% at 90% 80%, rgba(255,87,34,0.10) 0%, transparent 70%),
        radial-gradient(ellipse 40% 60% at 50% 50%, rgba(25,118,210,0.06) 0%, transparent 80%)
      `,
    },
    wrap: { position:'relative', zIndex:1, maxWidth:480, margin:'0 auto', padding:'0 20px 80px' },
    topbar: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 0 24px' },
    logoText: { fontWeight:800, fontSize:18, letterSpacing:'-0.5px' },
    vBadge: {
      fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:20,
      background:'rgba(13,71,161,0.25)', color:'#90caf9',
      border:'1px solid rgba(13,71,161,0.4)', letterSpacing:'0.04em',
    },
    hero: {
      display:'flex', gap:18, alignItems:'flex-start',
      padding:'24px', marginBottom:12,
      background:'#141720', borderRadius:20, border:'1px solid rgba(255,255,255,0.07)',
      animation:'fadeUp .5s ease both',
    },
    appIcon: {
      width:88, height:88, borderRadius:22, flexShrink:0,
      overflow:'hidden', background:'#0e1117',
      boxShadow:'0 8px 32px rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.07)',
    },
    statsRow: {
      display:'grid', gridTemplateColumns:'repeat(3,1fr)',
      gap:1, background:'rgba(255,255,255,0.07)',
      borderRadius:16, overflow:'hidden', marginBottom:12,
      border:'1px solid rgba(255,255,255,0.07)',
      animation:'fadeUp .5s .08s ease both',
    },
    stat: { background:'#141720', padding:'16px 12px', textAlign:'center' },
    statVal: { fontWeight:800, fontSize:22, color:'#f0f2f8', fontFamily:"'Syne', sans-serif" },
    statLabel: { fontSize:10, color:'#8892a4', marginTop:3, textTransform:'uppercase', letterSpacing:'0.06em' },
    statusCard: {
      padding:'18px 20px', borderRadius:16, marginBottom:12,
      border:`1px solid ${statusCfg.cardBorder}`,
      background: statusCfg.cardBg,
      animation:'fadeUp .5s .14s ease both',
    },
    sectionTitle: {
      fontSize:11, fontWeight:700, color:'#8892a4',
      textTransform:'uppercase', letterSpacing:'0.08em',
      marginBottom:10, marginTop:20,
    },
    card: {
      background:'#141720', borderRadius:14,
      border:'1px solid rgba(255,255,255,0.07)', overflow:'hidden',
    },
    versionItem: (isLatest) => ({
      display:'flex', alignItems:'center', gap:14,
      padding:'13px 16px',
      background: isLatest ? 'rgba(34,197,94,0.04)' : 'transparent',
      borderBottom:'1px solid rgba(255,255,255,0.05)',
      cursor:'pointer', transition:'background .15s',
    }),
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={S.mesh} />
      <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#0D47A1,#1976D2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, animation:'pulse 1.5s infinite' }}>📱</div>
      <div style={{ color:'#8892a4', fontSize:14 }}>Loading app...</div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} } @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );

  const sortedVersions = [...(app?.apkVersions || [])].sort((a,b) => b.versionCode - a.versionCode);

  return (
    <div style={S.page}>
      <div style={S.mesh} />

      {/* API banner */}
      {apiBanner && (
        <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:9998, padding:'10px 16px', background:'#1e3a5f', color:'#93c5fd', fontSize:12, fontFamily:'monospace', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, borderBottom:'1px solid rgba(25,118,210,0.4)' }}>
          <span>{apiBanner}</span>
          <button onClick={() => setApiBanner(null)} style={{ background:'none', border:'none', color:'#93c5fd', cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
        </div>
      )}

      {/* Non-Android notice */}
      {!isAndroid && (
        <div style={{ background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:12, margin:'0 20px 12px', padding:'12px 16px', display:'flex', gap:10, alignItems:'center', color:'#fcd34d', fontSize:13, zIndex:1, position:'relative' }}>
          <span style={{ fontSize:18 }}>ℹ️</span>
          <span>This app is for Android. Direct install is not available on your device.</span>
        </div>
      )}

      <div style={S.wrap}>
        {/* Topbar */}
        <div style={S.topbar}>
          <div style={S.logoText}>
            tracklio<span style={{ color:'#FF5722' }}>hub</span>
          </div>
          <div style={S.vBadge}>v{app?.version || '1.0'}</div>
        </div>

        {/* Hero */}
        <div style={S.hero}>
          <div style={S.appIcon}>
            {app?.iconUrl || app?.logoUrl ? (
              <img src={app.iconUrl || app.logoUrl} alt="App Icon"
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
              />
            ) : null}
            <div style={{ width:'100%', height:'100%', background:`linear-gradient(135deg, ${app?.primaryColor||'#0D47A1'}, ${app?.secondaryColor||'#1976D2'})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:36, display: (app?.iconUrl || app?.logoUrl) ? 'none' : 'flex' }}>
              📱
            </div>
          </div>

          <div style={{ flex:1, minWidth:0, paddingTop:2 }}>
            <div style={{ fontWeight:800, fontSize:22, letterSpacing:'-0.5px', lineHeight:1.1, marginBottom:6 }}>
              {app?.appName}
            </div>
            <div style={{ fontSize:11, color:'#8892a4', fontFamily:'monospace', marginBottom:10, wordBreak:'break-all' }}>
              {app?.packageName}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              <Tag label={app?.status || 'Active'} variant={app?.status === 'Building' ? 'orange' : 'green'} />
              <Tag label={`SDK ${app?.minSdkVersion}+`} variant="grey" />
              <Tag label={`Target ${app?.targetSdkVersion}`} variant="grey" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={S.statsRow}>
          {[
            { val: app?.builds    || 0,   label: 'Builds'    },
            { val: app?.downloads || 0,   label: 'Downloads' },
            { val: app?.version   || '—', label: 'Version'   },
          ].map(({ val, label }) => (
            <div key={label} style={S.stat}>
              <div style={S.statVal}>{val}</div>
              <div style={S.statLabel}>{label}</div>
            </div>
          ))}
        </div>

        {/* Status */}
        <div style={{ ...S.statusCard, animation:'fadeUp .5s .14s ease both' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:12, flexShrink:0, background:statusCfg.iconBg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
              {statusCfg.icon}
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:statusCfg.titleColor, marginBottom:3 }}>
                {statusCfg.title}
              </div>
              <div style={{ fontSize:12, color:'#8892a4', lineHeight:1.4 }}>{statusCfg.sub}</div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {downloading && (
          <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:14, padding:'16px 18px', marginBottom:12, animation:'fadeUp .3s ease both' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:13, fontWeight:600 }}>{progress < 100 ? 'Downloading...' : 'Complete!'}</span>
              <span style={{ fontSize:14, fontWeight:700, color:'#1976D2' }}>{progress}%</span>
            </div>
            <div style={{ height:6, background:'rgba(255,255,255,0.08)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${progress}%`, background:'linear-gradient(90deg,#1976D2,#42a5f5)', borderRadius:3, transition:'width .3s' }} />
            </div>
            <div style={{ fontSize:11, color:'#8892a4', marginTop:8 }}>{progressMsg}</div>
          </div>
        )}

        {/* CTA */}
        {installState !== 'unavailable' && (
          <div style={{ marginBottom:12, animation:'fadeUp .5s .2s ease both' }}>
            <button
              onClick={handleMainAction}
              disabled={downloading}
              style={{
                width:'100%', padding:'16px 24px', borderRadius:14, border:'none',
                fontWeight:700, fontSize:16, cursor: downloading ? 'not-allowed' : 'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                color:'#fff', opacity: downloading ? .6 : 1, transition:'all .2s',
                position:'relative', overflow:'hidden',
                ...cta.style,
              }}
            >
              <span>{cta.icon}</span>
              <span>{downloading ? 'Downloading...' : cta.label}</span>
              {/* Shimmer on install */}
              {installState === 'not_installed' && (
                <span style={{ position:'absolute', top:0, left:'-100%', width:'60%', height:'100%', background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)', animation:'shimmer 2.5s infinite' }} />
              )}
            </button>

            {/* Secondary button */}
            {(installState === 'update' || installState === 'installed') && (
              <button
                onClick={installState === 'update' ? openApp : () => showToast('✅ You have the latest version!', 'success')}
                style={{ width:'100%', marginTop:8, padding:'12px 24px', borderRadius:14, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', color:'#8892a4', fontSize:14, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
              >
                {installState === 'update' ? '🚀 Open Current Version' : '🔍 Check for Updates'}
              </button>
            )}
          </div>
        )}

        {/* About */}
        <div style={S.sectionTitle}>About</div>
        <div style={{ ...S.card, padding:'16px 18px', fontSize:13, color:'#8892a4', lineHeight:1.7, marginBottom:0, animation:'fadeUp .5s .28s ease both' }}>
          {app?.description || 'No description available.'}
        </div>

        {/* Features */}
        {(app?.features?.length > 0) && (
          <>
            <div style={S.sectionTitle}>Features</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, animation:'fadeUp .5s .32s ease both' }}>
              {app.features.map(f => (
                <div key={f} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 13px', borderRadius:20, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', fontSize:12, color:'#8892a4' }}>
                  <span style={{ fontSize:14 }}>{FEATURE_ICONS[f] || '⚡'}</span>{f}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Version history */}
        <div style={S.sectionTitle}>All Versions</div>
        <div style={{ ...S.card, animation:'fadeUp .5s .36s ease both' }}>
          {sortedVersions.length === 0 ? (
            <div style={{ padding:'24px', textAlign:'center', color:'#8892a4', fontSize:13 }}>No versions uploaded yet.</div>
          ) : (
            sortedVersions.map((v, i) => {
              const isLatest  = v._id === (app?.latestAPK?._id || latestAPK?._id);
              const trackMeta = TRACK_META[v.track] || TRACK_META.production;
              return (
                <div key={v._id || i}
                  style={S.versionItem(isLatest)}
                  onMouseEnter={e => { if (!isLatest) e.currentTarget.style.background='rgba(25,118,210,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background= isLatest ? 'rgba(34,197,94,0.04)' : 'transparent'; }}
                >
                  {/* Track badge */}
                  <div style={{ width:36, height:36, borderRadius:9, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, background:trackMeta.bg, color:trackMeta.color }}>
                    {trackMeta.label}
                  </div>

                  {/* Info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'#f0f2f8', marginBottom:2, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      v{v.versionName} ({v.versionCode})
                      {isLatest && <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:10, background:'rgba(34,197,94,0.15)', color:'#86efac', border:'1px solid rgba(34,197,94,0.25)', textTransform:'uppercase', letterSpacing:'0.05em' }}>LATEST</span>}
                      {!v.isActive && <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:10, background:'rgba(255,255,255,0.06)', color:'#8892a4', textTransform:'uppercase', letterSpacing:'0.05em' }}>INACTIVE</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#8892a4' }}>
                      {v.track} · {v.fileSize} · {fmtDate(v.uploadedAt)}
                    </div>
                  </div>

                  {/* Download btn */}
                  <button
                    onClick={() => handleSpecificDownload(v._id, v.fileName, v.versionCode)}
                    disabled={downloading}
                    style={{ fontSize:12, color:'#90caf9', padding:'5px 12px', border:'1px solid rgba(25,118,210,0.3)', borderRadius:20, fontWeight:600, background:'transparent', cursor: downloading ? 'not-allowed' : 'pointer', flexShrink:0, opacity: downloading ? .5 : 1 }}
                  >
                    ⬇
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Build info */}
        {app?.buildHistory?.length > 0 && (
          <>
            <div style={S.sectionTitle}>Build History</div>
            <div style={{ ...S.card, animation:'fadeUp .5s .4s ease both' }}>
              {app.buildHistory.map((b, i) => (
                <div key={b._id || i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderBottom: i < app.buildHistory.length-1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background: b.status==='Success'?'#22c55e': b.status==='Building'?'#f59e0b':'#ef4444' }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500, color:'#f0f2f8' }}>v{b.version} · {b.platform} · {b.buildType}</div>
                    <div style={{ fontSize:11, color:'#8892a4', marginTop:2 }}>{fmtDate(b.startedAt)}</div>
                  </div>
                  <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:12,
                    background: b.status==='Success'?'rgba(34,197,94,0.15)': b.status==='Building'?'rgba(245,158,11,0.15)':'rgba(239,68,68,0.15)',
                    color:      b.status==='Success'?'#86efac': b.status==='Building'?'#fcd34d':'#fca5a5',
                  }}>
                    {b.status}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{ textAlign:'center', paddingTop:32, fontSize:11, color:'rgba(136,146,164,0.4)', animation:'fadeUp .5s .44s ease both' }}>
          {app?.packageName} &nbsp;·&nbsp; Min Android {app?.minSdkVersion}+
        </div>
      </div>

      {/* Toast */}
      <Toast {...toast} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Epilogue:wght@400;500;600;700&display=swap');
        @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { to { left:150%; } }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.4} }
        * { box-sizing: border-box; }
        body { margin:0; }
      `}</style>
    </div>
  );
}

// ─── Demo data fallback ────────────────────────────────────
const DEMO_DATA = {
  "_id": "69eb4f934aca106a7c0c6b4c",
  "appName": "DPC Application",
  "packageName": "com.trackliohub.tracklio",
  "description": "DPC Application for Android device provisioning and management. Enables zero-touch enrollment for enterprise Android deployments.",
  "primaryColor": "#0D47A1", "secondaryColor": "#1976D2", "accentColor": "#FF5722",
  "version": "1.0.0", "minSdkVersion": "21", "targetSdkVersion": "33",
  "features": ["Push Notifications", "Offline Mode"],
  "iconUrl": "https://res.cloudinary.com/daeuia2lp/image/upload/v1777029010/trackliohub/images/IMG-logo-1777029008891-8DB0.png",
  "logoUrl": "https://res.cloudinary.com/daeuia2lp/image/upload/v1777029010/trackliohub/images/IMG-logo-1777029008827-385H.png",
  "builds": 1, "downloads": 9, "status": "Building", "isActive": true,
  "apkVersions": [
    { "_id":"69ec20beb54db6ee0c61cb3f","versionName":"1.0.0","versionCode":1,"track":"production","fileSize":"11.0 MB","isActive":false,"uploadedAt":"2026-04-25T02:02:38.076Z","apkUrl":"http://trackliohub.com/uploads/apk/APK-stepone-release-1777082557795.apk","fileName":"stepone-release-v1.apk" },
    { "_id":"69ec216bb54db6ee0c61cb9a","versionName":"1.0.0","versionCode":2,"track":"production","fileSize":"11.0 MB","isActive":true,"uploadedAt":"2026-04-25T02:05:31.161Z","apkUrl":"http://trackliohub.com/uploads/apk/APK-stepone-release-1777082730917.apk","fileName":"stepone-release-v2.apk" },
    { "_id":"6a0d583cbc8887df5a47edf1","versionName":"1.0.0","versionCode":2,"track":"alpha","fileSize":"80.4 MB","isActive":true,"uploadedAt":"2026-05-20T06:44:12.455Z","apkUrl":"http://trackliohub.com/uploads/apk/APK-app-release-1779259451760.apk","fileName":"app-release-alpha.apk" },
  ],
  "latestAPK": { "_id":"69ec216bb54db6ee0c61cb9a","versionName":"1.0.0","versionCode":2,"track":"production","fileSize":"11.0 MB","isActive":true,"uploadedAt":"2026-04-25T02:05:31.161Z","apkUrl":"http://trackliohub.com/uploads/apk/APK-stepone-release-1777082730917.apk","fileName":"stepone-release-v2.apk" },
  "buildHistory": [
    { "_id":"69eb7ae24aca106a7c0c6ca5","version":"1.0.0","status":"Building","platform":"android","buildType":"release","startedAt":"2026-04-24T14:14:58.433Z" }
  ],
};
